import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Pagination,
  Progress,
  Segment,
  Select,
  Table
} from 'semantic-ui-react';
import { API, isRoot, showError, showInfo, showSuccess, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const statusColor = {
  completed: 'green',
  pending: 'yellow',
  failed: 'red'
};

const statusText = {
  completed: '已迁移',
  pending: '迁移中',
  failed: '失败'
};

const jobStatusText = {
  queued: '排队中',
  running: '迁移中',
  completed: '已完成',
  partial: '部分失败',
  failed: '失败',
  panicked: '异常终止'
};

const SeedanceAssets = () => {
  const [userKeyword, setUserKeyword] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [userId, setUserId] = useState('');

  const [sourceChannels, setSourceChannels] = useState([]);
  const [sourceChannelId, setSourceChannelId] = useState('');
  const [targetChannels, setTargetChannels] = useState([]);
  const [targetChannelId, setTargetChannelId] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');

  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [migratingId, setMigratingId] = useState('');
  const [inspectingId, setInspectingId] = useState('');
  const [assetDetail, setAssetDetail] = useState(null);
  const [startingBulk, setStartingBulk] = useState(false);
  const [job, setJob] = useState(null);

  const sourceOptions = useMemo(
    () => sourceChannels.map((channel) => ({
      key: channel.id,
      value: channel.id,
      text: `${channel.name || '未命名渠道'}（ID ${channel.id}）`
    })),
    [sourceChannels]
  );

  const targetOptions = useMemo(
    () => targetChannels.map((channel) => ({
      key: channel.id,
      value: channel.id,
      text: `${channel.name || '未命名渠道'}（ID ${channel.id}${channel.project_name ? `，项目 ${channel.project_name}` : ''}）`
    })),
    [targetChannels]
  );

  const groupOptions = useMemo(
    () => [
      { key: 'all', value: '', text: '全部素材组' },
      ...groups.map((group) => ({
        key: group.group_id,
        value: group.group_id,
        text: `${group.group_id}（${group.asset_count} 个，已迁移 ${group.migrated_count}）`
      }))
    ],
    [groups]
  );

  const searchUsers = async () => {
    if (!userKeyword.trim()) {
      showInfo('请输入用户名、邮箱或显示名称');
      return;
    }
    setSearchingUsers(true);
    try {
      const res = await API.get(`/api/user/search?keyword=${encodeURIComponent(userKeyword.trim())}`);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      const options = (data || []).map((user) => ({
        key: user.id,
        value: user.id,
        text: `${user.username || user.display_name || '用户'}（ID ${user.id}）`
      }));
      setUserOptions(options);
      if (options.length === 1) {
        changeUser(options[0].value);
      }
      if (options.length === 0) {
        showInfo('没有找到匹配用户');
      }
    } catch (error) {
      showError(error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const loadSourceChannels = async (selectedUserId) => {
    const res = await API.get(`/api/seedance_assets/source_channels?user_id=${selectedUserId}`);
    const { success, message, data } = res.data;
    if (!success) {
      showError(message);
      return;
    }
    setSourceChannels(data.items || []);
  };

  const loadTargetChannels = async (sourceId) => {
    const query = sourceId ? `?exclude_channel_id=${sourceId}` : '';
    const res = await API.get(`/api/seedance_assets/target_channels${query}`);
    const { success, message, data } = res.data;
    if (!success) {
      showError(message);
      return;
    }
    setTargetChannels(data.items || []);
  };

  const loadGroups = async (selectedUserId, channelId) => {
    if (!selectedUserId || !channelId) {
      setGroups([]);
      return;
    }
    const res = await API.get(`/api/seedance_assets/groups?user_id=${selectedUserId}&channel_id=${channelId}`);
    const { success, message, data } = res.data;
    if (!success) {
      showError(message);
      return;
    }
    setGroups(data.items || []);
  };

  const loadAssets = async (selectedUserId, channelId, selectedGroupId, page) => {
    if (!selectedUserId || !channelId) {
      setAssets([]);
      setTotal(0);
      return;
    }
    setLoadingAssets(true);
    try {
      const params = new URLSearchParams({
        user_id: String(selectedUserId),
        channel_id: String(channelId),
        p: String(page)
      });
      if (selectedGroupId) params.set('group_id', selectedGroupId);
      const res = await API.get(`/api/seedance_assets/assets?${params.toString()}`);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setAssets(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      showError(error);
    } finally {
      setLoadingAssets(false);
    }
  };

  const changeUser = async (selectedUserId) => {
    setUserId(selectedUserId);
    setSourceChannelId('');
    setTargetChannelId('');
    setGroupId('');
    setAssets([]);
    setGroups([]);
    setTotal(0);
    setJob(null);
    setAssetDetail(null);
    await loadSourceChannels(selectedUserId);
    await loadTargetChannels('');
  };

  const changeSourceChannel = async (selectedChannelId) => {
    setSourceChannelId(selectedChannelId);
    setTargetChannelId('');
    setGroupId('');
    setAssetDetail(null);
    setActivePage(1);
    await Promise.all([
      loadTargetChannels(selectedChannelId),
      loadGroups(userId, selectedChannelId)
    ]);
    await loadAssets(userId, selectedChannelId, '', 0);
  };

  const changeGroup = async (selectedGroupId) => {
    setGroupId(selectedGroupId);
    setActivePage(1);
    await loadAssets(userId, sourceChannelId, selectedGroupId, 0);
  };

  const refresh = async () => {
    await Promise.all([
      loadGroups(userId, sourceChannelId),
      loadAssets(userId, sourceChannelId, groupId, activePage - 1)
    ]);
  };

  const migrateOne = async (assetId) => {
    if (!targetChannelId) {
      showInfo('请先选择目标渠道');
      return;
    }
    setMigratingId(assetId);
    try {
      const res = await API.post('/api/seedance_assets/migrate', {
        user_id: Number(userId),
        source_channel_id: Number(sourceChannelId),
        target_channel_id: Number(targetChannelId),
        asset_id: assetId
      });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      showSuccess(`素材已迁移到 ${data.target_asset_id}`);
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setMigratingId('');
    }
  };

  const inspectAsset = async (assetId) => {
    setInspectingId(assetId);
    try {
      const params = new URLSearchParams({
        user_id: String(userId),
        channel_id: String(sourceChannelId),
        asset_id: assetId
      });
      const res = await API.get(`/api/seedance_assets/asset?${params.toString()}`);
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setAssetDetail({ id: assetId, data });
    } catch (error) {
      showError(error);
    } finally {
      setInspectingId('');
    }
  };

  const migrateAll = async () => {
    if (!targetChannelId) {
      showInfo('请先选择目标渠道');
      return;
    }
    if (!window.confirm(`确认把当前筛选下的 ${total} 个素材迁移到目标渠道？源素材不会被删除。`)) {
      return;
    }
    setStartingBulk(true);
    try {
      const res = await API.post('/api/seedance_assets/migrate_all', {
        user_id: Number(userId),
        source_channel_id: Number(sourceChannelId),
        target_channel_id: Number(targetChannelId),
        group_id: groupId
      });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setJob(data);
      showSuccess('批量迁移任务已启动');
    } catch (error) {
      showError(error);
    } finally {
      setStartingBulk(false);
    }
  };

  useEffect(() => {
    if (!job || !job.id || !['queued', 'running'].includes(job.status)) {
      return undefined;
    }
    const timer = window.setInterval(async () => {
      try {
        const res = await API.get(`/api/seedance_assets/jobs/${job.id}`);
        const { success, message, data } = res.data;
        if (!success) {
          showError(message);
          return;
        }
        setJob(data);
        if (!['queued', 'running'].includes(data.status)) {
          window.clearInterval(timer);
          await refresh();
        }
      } catch (error) {
        showError(error);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [job?.id, job?.status]);

  const migrationHistory = (asset) => {
    const rows = (asset.migrations || []).filter((migration) => {
      return !targetChannelId || migration.target_channel_id === Number(targetChannelId);
    });
    if (rows.length === 0) {
      return <Label basic size='small'>未迁移</Label>;
    }
    return rows.map((migration) => (
      <Label
        key={migration.id}
        color={statusColor[migration.status] || 'grey'}
        size='small'
        title={migration.error || `目标素材：${migration.target_asset_id || '-'}`}
      >
        #{migration.target_channel_id} {statusText[migration.status] || migration.status}
      </Label>
    ));
  };

  const isCurrentTargetCompleted = (asset) => {
    return (asset.migrations || []).some((migration) => {
      return migration.target_channel_id === Number(targetChannelId) && migration.status === 'completed';
    });
  };

  const jobTotal = job?.total || 0;
  const jobPercent = jobTotal > 0 ? Math.round(((job?.completed || 0) / jobTotal) * 100) : 0;

  if (!isRoot()) {
    return (
      <Segment>
        <Message negative>仅超级管理员可访问该页面。</Message>
      </Segment>
    );
  }

  return (
    <>
      <Segment>
        <Header as='h3'>Seedance 资产</Header>
        <Message info>
          将用户在某条 Seedance 渠道创建的素材复制到另一条渠道。源素材不会被删除；生成请求遇到其他渠道的素材时会自动尝试迁移。
        </Message>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>搜索用户</label>
              <Input
                placeholder='用户名 / 邮箱 / 显示名称'
                value={userKeyword}
                onChange={(event) => setUserKeyword(event.target.value)}
                action={{ color: 'blue', content: '搜索', loading: searchingUsers, onClick: searchUsers }}
              />
            </Form.Field>
            <Form.Field>
              <label>用户</label>
              <Select
                search
                placeholder='选择用户'
                options={userOptions}
                value={userId}
                onChange={(_, { value }) => changeUser(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>源渠道</label>
              <Select
                search
                disabled={!userId}
                placeholder='选择素材所在渠道'
                options={sourceOptions}
                value={sourceChannelId}
                onChange={(_, { value }) => changeSourceChannel(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>目标渠道</label>
              <Select
                search
                disabled={!sourceChannelId}
                placeholder='选择迁移目标渠道'
                options={targetOptions}
                value={targetChannelId}
                onChange={(_, { value }) => setTargetChannelId(value)}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>素材组</label>
              <Select
                disabled={!sourceChannelId}
                options={groupOptions}
                value={groupId}
                onChange={(_, { value }) => changeGroup(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>&nbsp;</label>
              <Button
                icon
                labelPosition='left'
                color='teal'
                disabled={!sourceChannelId || !targetChannelId || total === 0}
                loading={startingBulk}
                onClick={migrateAll}
              >
                <Icon name='copy' />
                批量迁移当前筛选
              </Button>
            </Form.Field>
            <Form.Field>
              <label>&nbsp;</label>
              <Button icon labelPosition='left' onClick={refresh} disabled={!sourceChannelId}>
                <Icon name='refresh' />
                刷新
              </Button>
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {job && (
        <Segment>
          <Header as='h4'>
            批量任务 {jobStatusText[job.status] || job.status}
            <Label basic>{job.id}</Label>
          </Header>
          <Progress
            percent={jobPercent}
            color={job.failed > 0 ? 'orange' : 'green'}
            indicating={['queued', 'running'].includes(job.status)}
            progress={`${job.completed || 0} / ${job.total || 0}`}
          />
          <div>
            成功 {job.succeeded || 0}，失败 {job.failed || 0}
            {job.current_asset_id ? `，当前素材 ${job.current_asset_id}` : ''}
          </div>
          {(job.errors || []).length > 0 && (
            <Message warning>
              {job.errors.map((error, index) => <div key={index}>{error}</div>)}
            </Message>
          )}
        </Segment>
      )}

      <Segment loading={loadingAssets}>
        <Table celled compact selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>素材 ID</Table.HeaderCell>
              <Table.HeaderCell>素材组</Table.HeaderCell>
              <Table.HeaderCell>创建时间</Table.HeaderCell>
              <Table.HeaderCell>迁移记录</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {assets.map((asset) => (
              <Table.Row key={asset.id}>
                <Table.Cell style={{ fontFamily: 'monospace' }}>{asset.id}</Table.Cell>
                <Table.Cell style={{ fontFamily: 'monospace' }}>{asset.group_id}</Table.Cell>
                <Table.Cell>{timestamp2string(new Date(asset.created_at).getTime() / 1000)}</Table.Cell>
                <Table.Cell>{migrationHistory(asset)}</Table.Cell>
                <Table.Cell>
                  <Button
                    size='small'
                    basic
                    color='blue'
                    loading={inspectingId === asset.id}
                    onClick={() => inspectAsset(asset.id)}
                  >
                    查看
                  </Button>
                  <Button
                    size='small'
                    color='blue'
                    style={{ marginLeft: '0.5em' }}
                    loading={migratingId === asset.id}
                    disabled={!targetChannelId || isCurrentTargetCompleted(asset)}
                    onClick={() => migrateOne(asset.id)}
                  >
                    迁移
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
            {assets.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan='5' textAlign='center'>暂无素材</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Pagination
            activePage={activePage}
            totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
            onPageChange={(_, { activePage: page }) => {
              setActivePage(page);
              loadAssets(userId, sourceChannelId, groupId, page - 1);
            }}
          />
        </div>
      </Segment>

      <Modal open={!!assetDetail} onClose={() => setAssetDetail(null)} size='large' closeIcon>
        <Modal.Header>素材详情 {assetDetail?.id}</Modal.Header>
        <Modal.Content scrolling>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {assetDetail ? JSON.stringify(assetDetail.data, null, 2) : ''}
          </pre>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setAssetDetail(null)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default SeedanceAssets;
